import { type ReactNode } from "react";

import { NorthbeamUtmPlatform } from "../configs";
import { LegacyUtmPlatform } from "../constants";

import { UtmAccordion } from "./utm-accordion";

const LegacyUtmDetail = ({
  platform,
  children,
}: {
  platform: NorthbeamUtmPlatform;
  children: ReactNode;
}): JSX.Element => (
  <div className="legacy-utm-detail flex flex-col gap-3">
    <p className="mb-0 font-semibold">{platform}</p>
    <div className="legacy-utm-detail__content flex flex-col gap-3 pl-2">
      {children}
    </div>
  </div>
);

const LEGACY_UTM_COMPONENTS: { [key in LegacyUtmPlatform]: JSX.Element } = {
  [LegacyUtmPlatform.WICKED_REPORTS]: (
    <LegacyUtmDetail platform={NorthbeamUtmPlatform.META}>
      <code>WickedSource=Facebook&WickedID=$AD_ID</code>
    </LegacyUtmDetail>
  ),
  [LegacyUtmPlatform.HYROS]: (
    <LegacyUtmDetail platform={NorthbeamUtmPlatform.META}>
      <code>fbc_id=$ADSET_ID&h_ad_id=$AD_ID</code>
    </LegacyUtmDetail>
  ),
  [LegacyUtmPlatform.ROCKERBOX]: (
    <LegacyUtmDetail platform={NorthbeamUtmPlatform.META}>
      <code>fb_campaignid=$CAMPAIGN_ID&fb_adsetid=$ADSET_ID&fb_adid$AD_ID</code>
    </LegacyUtmDetail>
  ),
  [LegacyUtmPlatform.TRIPLE_WHALE]: (
    <div className="flex flex-col gap-3">
      <p className="mb-0">
        We ingest any Facebook and TikTok UTMs from Triple Whale.
      </p>
      <LegacyUtmDetail platform={NorthbeamUtmPlatform.META}>
        <div className="flex flex-col gap-2">
          <p className="mb-0">
            One of the following source parameters + values is required:
          </p>
          <ul className="pl-4 mb-0">
            <li>
              tw_source = <code>{"{{ site_source_name }}"}</code>
            </li>
            <li>
              tw_source = <code>facebook</code>
            </li>
            <li>
              utm_source = <code>facebook</code>
            </li>
            <li>
              utm_source = <code>{"{{ site_source_name }}"}</code>
            </li>
          </ul>
        </div>
        <div className="flex flex-col gap-2">
          <p className="mb-0 font-semibold text-sm">AND</p>
          <p className="mb-0">
            One of the following adid parameters + values is required:
          </p>
          <ul className="pl-4 mb-0">
            <li>
              tw_adid = <code>{"{{ad.id}}"}</code>
            </li>
            <li>
              fbadid = <code>{"{{ad.id}}"}</code>
            </li>
          </ul>
        </div>
      </LegacyUtmDetail>
      <LegacyUtmDetail platform={NorthbeamUtmPlatform.TIKTOK}>
        <p className="mb-0">
          The following source parameters + values is required:
        </p>
        <ul className="mb-0">
          <li>
            tw_source = <code>tiktok&tw_adid=_CID_</code>
          </li>
        </ul>
      </LegacyUtmDetail>
    </div>
  ),
};

export const LegacyUtms = (): JSX.Element => {
  return (
    <div className="legacy-utms flex flex-col gap-3">
      <div className="flex flex-col">
        <h4 className="heading mx-0 mb-3 mt-0">Legacy UTMs</h4>
        <p className="mb-0">
          We ingest some legacy UTMs.&nbsp;
          <span className="font-semibold">
            If you have previously placed the UTMs listed below, we can use
            them.
          </span>
        </p>
        <p className="mb-0">
          You don&apos;t have to update your historic ads if they have these
          UTMs in place.
        </p>
      </div>
      <UtmAccordion
        data={Object.values(LegacyUtmPlatform).map((platform, i) => ({
          title: platform,
          content: LEGACY_UTM_COMPONENTS[platform],
        }))}
      />
      <small>
        For any ads currently running with these UTMs, Northbeam will
        automatically track them.
        <br />
        <strong>Please note</strong>, any new ads launched should use
        Northbeam`s UTMs.
      </small>
    </div>
  );
};
